<template>
  <table v-if="processedRecords.length !== 0">
    <colgroup>
      <col class="min-w-[35px]">
      <col class="w-full sm:w-[60%] md:w-[35%]">
      <col class="w-[10%] hidden md:table-column">
      <col class="w-[40%] hidden md:w-[25%] sm:table-column">
      <col class="w-[30%] hidden md:table-column">
    </colgroup>
    <thead>
      <tr class="border-b-2 border-gray-200 text-left">
        <th scope="col" />
        <th scope="col" class="py-1.5 font-medium">IP address</th>
        <th scope="col" class="py-1.5 font-medium hidden md:table-cell">Type</th>
        <th scope="col" class="py-1.5 font-medium hidden sm:table-cell">Hosted by</th>
        <th scope="col" class="py-1.5 font-medium hidden md:table-cell">Location</th>
      </tr>
    </thead>
    <tbody>
      <template v-for="(record, index) in processedRecords" :key="index">
        <tr class="group">
          <td v-if="record.ipInfo" class="text-center" @click.stop="e => { e.target?.closest('tr').nextSibling.classList.toggle('hidden'); e.target?.closest('tr').querySelector('svg').classList.toggle('rotate-90') }">
            <svg width="1em" height="1em" viewBox="0 0 16 16" class="inline" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </td>
          <td v-else />
          <td class="py-1">
            <strong class="break-all">{{ getIp(record.record) }}</strong>
            <copy :content="getIp(record.record)" class="[visibility:hidden] group-hover:visible" />
          </td>
          <td class="hidden md:table-cell">
            {{ record.record.recordType === 'A' ? 'IPv4' : 'IPv6'}}
          </td>
          <td class="hidden sm:table-cell">
            <img v-if="record.ipInfo" class="inline w-4 h-4 mb-0.5 mr-1.5" :src="`https://icons.duckduckgo.com/ip3/${record.ipInfo.asdomain}.ico`"
                 :alt="`Hosted by ${record.ipInfo.asname}`" :title="`Hosted by ${record.ipInfo.asname}`">
            {{ record.ipInfo?.asname }}
          </td>
          <td class="hidden md:table-cell">
            {{ record.ipInfo?.country }}
          </td>
        </tr>
        <tr class="hidden" v-if="record.ipInfo">
          <td colspan="5">
            <div class="flex flex-col items-stretch bg-neutral-50 rounded-sm overflow-hidden md:flex-row">
              <div class="md:w-1/2">
                <span class="block m-6 ml-8 mb-4 text-lg font-medium">{{ record.ipInfo.org }}</span>
                <table class="w-full mx-6 md:mb-4 border-separate border-spacing-2">
                  <tbody>
                    <tr>
                      <th scope="row" class="text-left font-medium">Location</th>
                      <td><a :href="`https://www.google.com/maps/search/${record.ipInfo.city}, ${record.ipInfo.regionName}, ${record.ipInfo.country}`" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 hover:underline">{{ record.ipInfo.city }}, {{ record.ipInfo.regionName }}, {{ record.ipInfo.country }}</a></td>
                    </tr>
                    <tr>
                      <th scope="row" class="text-left font-medium">AS</th>
                      <td>{{ record.ipInfo.as }}</td>
                    </tr>
                    <tr>
                      <th scope="row" class="text-left font-medium whitespace-nowrap">AS name</th>
                      <td>{{ record.ipInfo.asname }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="w-full p-4 md:w-1/2">
                <div class="min-h-[140px] h-full w-full rounded-sm bg-center" :style="{ backgroundImage: `url(https://stadiamaps.com/static/outdoors?api_key=23fe682b-ac7c-4f2b-83db-39dcf2a8b9ac&amp;center=${record.ipInfo.lat + 2},${record.ipInfo.lon}&amp;zoom=2&amp;markers=${record.ipInfo.lat},${record.ipInfo.lon}&amp;size=1068x534 )` }" />
              </div>
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
  <div v-else>No IP addresses records found.</div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import Copy from '@/components/micro/CopyComponent.vue';
import {
  A, AAAA, QueryResult, RecordInfo,
} from '@/model/Api';

const props = defineProps({
  aRecords: {
    type: Object as PropType<QueryResult>,
    required: true,
  },
  aaaaRecords: {
    type: Object as PropType<QueryResult>,
    required: true,
  },
});

type IpRecordWithInfo = {
  record: A | AAAA;
} & RecordInfo

const processedRecords = computed(() => props.aRecords.response.answer.concat(props.aaaaRecords.response.answer).filter((item) => ['A', 'AAAA'].includes(item.record.recordType)) as Array<IpRecordWithInfo>);

function getIp(record: A | AAAA): string {
  if ('ipv4' in record) return record.ipv4;
  return record.ipv6.slice(1, -1);
}
</script>
